import React, { useState, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, X, User, Mail, Phone, Calendar, Briefcase, MapPin, Building2, ArrowLeft } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { formatDateStandard } from "@servify/shared-utils";

export default function Profile() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch current user data
  const { data: userData, isLoading } = useQuery({
    queryKey: ["/auth/me"],
  });

  const user = (userData as any)?.user;

  // Set image preview when user data loads
  useEffect(() => {
    if (user?.profilePictureUrl) {
      setImagePreview(user.profilePictureUrl);
    }
  }, [user]);

  // Upload image to Firebase
  const uploadImageToFirebase = async (file: File): Promise<string> => {
    try {
      setIsUploading(true);
      const { storage, ensureAuthenticated } = await import('@/lib/firebase');
      const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
      
      await ensureAuthenticated();
      
      const timestamp = Date.now();
      const fileName = `user-photographs/${timestamp}-${file.name}`;
      const storageRef = ref(storage, fileName);
      
      const snapshot = await uploadBytes(storageRef, file, {
        contentType: file.type
      });
      
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error: any) {
      console.error("Firebase upload error:", error);
      let errorMessage = "Failed to upload image. Please try again.";
      if (error?.code === 'storage/unauthorized') {
        errorMessage = "Authentication failed. Please refresh the page and try again.";
      } else if (error?.message) {
        errorMessage = `Upload failed: ${error.message}`;
      }
      toast({
        title: "Upload Error",
        description: errorMessage,
        variant: "destructive",
      });
      throw new Error(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  // Update profile mutation - use /auth/profile endpoint
  const updateProfileMutation = useMutation({
    mutationFn: async (profilePictureUrl: string) => {
      if (!user?.id) throw new Error("User ID not found");
      
      // Use the user profile endpoint (available for all authenticated users)
      const response = await apiRequest("PUT", `/auth/profile`, {
        profilePictureUrl,
      });
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Profile picture updated successfully",
      });
      queryClient.invalidateQueries({ queryKey: ['/auth/me'] });
      setSelectedImage(null);
    },
    onError: (error: any) => {
      console.error('Profile update error:', error);
      console.error('Error details:', {
        message: error?.message,
        response: error?.response,
        status: error?.response?.status,
        data: error?.response?.data
      });
      
      let errorMessage = "Failed to update profile picture";
      
      // Try to get error message from response
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.status === 403) {
        errorMessage = "You don't have permission to update your profile. Please contact your administrator.";
      } else if (error?.response?.status === 401) {
        errorMessage = "Please log in again to update your profile.";
      } else if (error?.response?.status === 400) {
        errorMessage = error?.response?.data?.message || "Invalid request. Please check your input.";
      } else if (error?.response?.status === 500) {
        errorMessage = "Server error. Please try again later or contact support.";
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      setIsUploading(false);
    },
  });

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Invalid File Type",
          description: "Please select an image file",
          variant: "destructive",
        });
        return;
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: "Image must be less than 5MB",
          variant: "destructive",
        });
        return;
      }
      
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(user?.profilePictureUrl || null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSaveProfilePicture = async () => {
    if (!selectedImage) {
      toast({
        title: "No Image Selected",
        description: "Please select an image to upload",
        variant: "destructive",
      });
      return;
    }

    try {
      const profilePictureUrl = await uploadImageToFirebase(selectedImage);
      updateProfileMutation.mutate(profilePictureUrl);
    } catch (error) {
      // Error already handled in uploadImageToFirebase
    }
  };

  const getInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
    }
    return "U";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-servify-purple mx-auto mb-4"></div>
          <p className="text-servify-dark-grey/70">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <p className="text-servify-dark-grey/70 mb-4">User not found</p>
          <Button 
            onClick={() => setLocation('/')}
            className="bg-servify-purple hover:bg-servify-purple-dark text-white"
          >
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-servify-dark-grey mb-2">
          My Profile
        </h1>
        <p className="text-servify-dark-grey/70">
          Manage your profile information and photo
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Picture Section */}
        <Card className="border-servify-white-grey shadow-lg hover:shadow-xl transition-shadow lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-servify-dark-grey">Profile Picture</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-center">
              {imagePreview ? (
                <div className="relative">
                  <Avatar className="w-32 h-32 border-4 border-servify-purple/20">
                    <AvatarImage src={imagePreview} alt="Profile" className="object-cover" />
                    <AvatarFallback className="text-2xl bg-servify-purple text-white">{getInitials()}</AvatarFallback>
                  </Avatar>
                </div>
              ) : (
                <Avatar className="w-32 h-32 border-4 border-servify-purple/20">
                  <AvatarFallback className="text-2xl bg-servify-purple text-white">{getInitials()}</AvatarFallback>
                </Avatar>
              )}
            </div>

            <div className="space-y-2">
              {imagePreview && selectedImage && (
                <div className="text-center space-y-3">
                  <p className="text-sm text-servify-dark-grey/70 font-medium">New image selected</p>
                  <div className="flex gap-2 justify-center">
                    <Button
                      onClick={handleSaveProfilePicture}
                      disabled={isUploading || updateProfileMutation.isPending}
                      className="bg-servify-purple hover:bg-servify-purple-dark text-white"
                    >
                      {isUploading || updateProfileMutation.isPending ? "Uploading..." : "Save Photo"}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleRemoveImage}
                      disabled={isUploading || updateProfileMutation.isPending}
                      className="border-servify-white-grey hover:bg-servify-white-grey"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {!selectedImage && (
                <div className="space-y-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef?.current?.click()}
                    disabled={isUploading || updateProfileMutation.isPending}
                    className="w-full border-servify-purple text-servify-purple hover:bg-servify-purple hover:text-white transition-colors"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {imagePreview ? "Change Photo" : "Upload Photo"}
                  </Button>
                  {imagePreview && (
                    <Button
                      variant="outline"
                      onClick={handleRemoveImage}
                      disabled={isUploading || updateProfileMutation.isPending}
                      className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Remove Photo
                    </Button>
                  )}
                </div>
              )}
            </div>

            <p className="text-xs text-servify-dark-grey/60 text-center">
              Max 5MB, JPG/PNG
            </p>
          </CardContent>
        </Card>

        {/* Personal Details Section */}
        <Card className="border-servify-white-grey shadow-lg hover:shadow-xl transition-shadow lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-servify-dark-grey">Personal Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-servify-dark-grey font-medium">
                  <div className="p-1.5 bg-servify-purple/10 rounded-lg">
                    <User className="h-4 w-4 text-servify-purple" />
                  </div>
                  Full Name
                </Label>
                <Input
                  value={`${user.firstName || ''} ${user.lastName || ''}`}
                  disabled
                  className="bg-servify-white-grey border-servify-white-grey text-servify-dark-grey"
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-servify-dark-grey font-medium">
                  <div className="p-1.5 bg-servify-purple/10 rounded-lg">
                    <Mail className="h-4 w-4 text-servify-purple" />
                  </div>
                  Email
                </Label>
                <Input
                  value={user.email || ''}
                  disabled
                  className="bg-servify-white-grey border-servify-white-grey text-servify-dark-grey"
                />
              </div>

              {user.dateOfJoining && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-servify-dark-grey font-medium">
                    <div className="p-1.5 bg-servify-purple/10 rounded-lg">
                      <Calendar className="h-4 w-4 text-servify-purple" />
                    </div>
                    Date of Joining
                  </Label>
                  <Input
                    value={formatDateStandard(new Date(user.dateOfJoining))}
                    disabled
                    className="bg-servify-white-grey border-servify-white-grey text-servify-dark-grey"
                  />
                </div>
              )}

              {user.department && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-servify-dark-grey font-medium">
                    <div className="p-1.5 bg-servify-purple/10 rounded-lg">
                      <Building2 className="h-4 w-4 text-servify-purple" />
                    </div>
                    Department
                  </Label>
                  <Input
                    value={user.department}
                    disabled
                    className="bg-servify-white-grey border-servify-white-grey text-servify-dark-grey"
                  />
                </div>
              )}

              {user.designation && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-servify-dark-grey font-medium">
                    <div className="p-1.5 bg-servify-purple/10 rounded-lg">
                      <Briefcase className="h-4 w-4 text-servify-purple" />
                    </div>
                    Designation
                  </Label>
                  <Input
                    value={user.designation}
                    disabled
                    className="bg-servify-white-grey border-servify-white-grey text-servify-dark-grey"
                  />
                </div>
              )}

              {user.workLocation && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-servify-dark-grey font-medium">
                    <div className="p-1.5 bg-servify-purple/10 rounded-lg">
                      <MapPin className="h-4 w-4 text-servify-purple" />
                    </div>
                    Work Location
                  </Label>
                  <Input
                    value={user.workLocation}
                    disabled
                    className="bg-servify-white-grey border-servify-white-grey text-servify-dark-grey"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-servify-dark-grey font-medium">
                  <div className="p-1.5 bg-servify-purple/10 rounded-lg">
                    <User className="h-4 w-4 text-servify-purple" />
                  </div>
                  Role
                </Label>
                <Input
                  value={user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : ''}
                  disabled
                  className="bg-servify-white-grey border-servify-white-grey text-servify-dark-grey"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

